import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CONTACT } from 'libs/core/src/lib/constants/contact.constant';
import { FormatUserPhonePipe } from 'libs/core/src/lib/pipes/format-user-phone.pipe';
import { UserService } from 'libs/core/src/lib/services/user.service';
import { SectionName, TRACKABLE_COMPONENT_NAME } from '@analytics';
import { SkeletonTemplateService } from '@skeleton-loader';
import { AlertMessageComponent } from '../alert-message/alert-message.component';
import { CapitalFirstLetterPipe, PostalCodePipe } from '../pipes';
import { ShippingDetails, UserAddress } from '@models';

@Component({
  selector: 'lib-shipping-information',
  templateUrl: './shipping-information.component.html',
  styleUrls: ['./shipping-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    FormatUserPhonePipe,
    AlertMessageComponent,
    CapitalFirstLetterPipe,
    PostalCodePipe,
  ],
  providers: [{ provide: TRACKABLE_COMPONENT_NAME, useValue: SectionName.Shipping }],
})
export class ShippingInformationComponent {
  readonly shippingAddress = input.required<UserAddress | ShippingDetails | null>();
  readonly email = input<string | null>();

  addressTooltip =
    'Shipments cannot be delivered to PO Box addresses, United States territories, or addresses outside of the United States.';

  readonly contact = CONTACT;

  readonly skeletonService = inject(SkeletonTemplateService);
  userService = inject(UserService);

  labels = computed(() => ({
    addressTooltip:
      'Shipments cannot be delivered to PO Box addresses, United States territories or addresses outside of the United States',
    notValidAddress: `It looks like this address is not valid for shipping. For more information, please call us at ${this.contact.PHONE}.`,
  }));

  userPhone = this.userService.telephoneNumber;
  name = this.userService.name;
  isValidAddress = this.userService.isValidAddress;

  address = computed(() => {
    const addr = this.shippingAddress();
    if (!addr) return null;
    return {
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2,
      addressLine3: addr.addressLine3,
      city: 'city' in addr ? addr.city : addr.addressCityName,
      state: 'state' in addr ? addr.state : addr.addressStateName,
      postalCode: 'postalCode' in addr ? addr.postalCode : addr.addressPostalCode,
      countryCode: 'countryCode' in addr ? addr.countryCode : addr.addressCountryCode,
    };
  });
}
